import { useState } from "react";
// @mui
import { Box, Typography } from "@mui/material";
import { LogOut as IconLogout, UserRound as IconUserRound } from "lucide-react";
import { styled, useTheme } from "@mui/material/styles";
// components
import MenuPopover from "components/MenuPopover";
import MyAvatar from "components/MyAvatar";
// icons
import ChevronDown from "assets/icon/chevron/ChevronDown";
import ChevronUpIcon from "assets/icon/chevron/up";
// other imports
import { useLogout } from "hooks/useLogout";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { NavBarAccountMenuItem } from "./NavBarAccountMenuItem";
import OverflowTooltip from "./OverFlowToolTip";

const RootStyle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  border: `1px solid ${theme.palette.text.borderLine}`,
  padding: theme.spacing(1, 1.5),
  borderRadius: Number(theme.shape.borderRadius) * 1,
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create("opacity", {
    duration: theme.transitions.duration.shorter,
  }),
}));

interface INavbarAccountProps {
  isCollapse: boolean | undefined;
}

export default function NavbarAccount({ isCollapse }: INavbarAccountProps) {
  const supplier_ = useSelector((state: any) => state.supplier);
  const supplier = supplier_?.supplier;
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { logout } = useLogout();

  const onClose = () => {
    setAnchorEl(null);
  };

  return (
    <RootStyle>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          width: "100%",
          cursor: "pointer",
          alignItems: "center",
          ...(isCollapse && { justifyContent: "center" }),
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        }}
      >
        <Box display="flex" gap={isCollapse ? 0 : 1.5} alignItems="center">
          <MyAvatar
            user={{ name: supplier?.name }}
            sx={{ width: 32, height: 32 }}
          />
          <Box
            sx={{
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.shorter,
                }),
              ...(isCollapse && { ml: 0, width: 0, overflow: "hidden" }),
            }}
          >
            <OverflowTooltip variant="subtitle2">
              {supplier?.name ?? ""}
            </OverflowTooltip>
            <OverflowTooltip
              variant="caption"
              sx={{ color: "text.label", fontWeight: 400 }}
            >
              {supplier?.email ?? ""}
            </OverflowTooltip>
          </Box>
        </Box>
        {!isCollapse && (
          <Box
            sx={{
              position: "relative",
              my: "auto",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          >
            {anchorEl === null ? (
              <ChevronDown height={20} width={20} />
            ) : (
              <ChevronUpIcon height={20} width={20} />
            )}
          </Box>
        )}
      </Box>
      <MenuPopover
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: 0, horizontal: 0 }}
        arrow="right-top"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mt: -1,
          width: 250,
          "& .MuiMenuItem-root": {
            px: 1,
            typography: "body2",
            borderRadius: 0.75,
          },
          backgroundColor: theme.palette.common.white,
          borderRadius: "12px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <NavBarAccountMenuItem
            title={t("My Profile")}
            startIcon={
              <IconUserRound strokeWidth={1.5} height={18} width={18} />
            }
            onClick={() => {
              navigate(`/${window.location.pathname.split("/")[1]}/account`);
              onClose();
            }}
          />
          <NavBarAccountMenuItem
            title={t("Logout")}
            startIcon={<IconLogout strokeWidth={1.5} height={18} width={18} />}
            onClick={() => {
              logout();
              onClose();
            }}
          />
        </Box>
      </MenuPopover>
    </RootStyle>
  );
}
